import { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
    customerLogin,
    customerRegister,
    customerLogout,
    fetchCustomer,
} from "./shopify";

const CustomerContext = createContext(null);
const TOKEN_KEY = "desashop_customer_token_v1";
const EXPIRES_KEY = "desashop_customer_expires_v1";

export function CustomerProvider({ children }) {
    const [token, setToken] = useState(
        () => localStorage.getItem(TOKEN_KEY) || null
    );
    const [customer, setCustomer] = useState(null);
    const [loading, setLoading] = useState(false);

    const isAuthed = Boolean(token && customer);

    const refresh = useCallback(async (tokenOverride) => {
        const t = tokenOverride || token;
        if (!t) {
            setCustomer(null);
            return;
        }
        setLoading(true);
        try {
            const c = await fetchCustomer(t);
            if (!c) {
                // token expired / invalid
                localStorage.removeItem(TOKEN_KEY);
                localStorage.removeItem(EXPIRES_KEY);
                setToken(null);
                setCustomer(null);
            } else {
                setCustomer(c);
            }
        } catch (e) {
            console.warn("Customer fetch failed", e);
            localStorage.removeItem(TOKEN_KEY);
            setToken(null);
            setCustomer(null);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        // Check expiry on mount
        const exp = localStorage.getItem(EXPIRES_KEY);
        if (exp && new Date(exp) < new Date()) {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(EXPIRES_KEY);
            setToken(null);
            return;
        }
        if (token) refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function login(email, password) {
        setLoading(true);
        try {
            const at = await customerLogin(email, password);
            localStorage.setItem(TOKEN_KEY, at.accessToken);
            localStorage.setItem(EXPIRES_KEY, at.expiresAt);
            setToken(at.accessToken);
            await refresh(at.accessToken);
            return true;
        } finally {
            setLoading(false);
        }
    }

    async function register(data) {
        setLoading(true);
        try {
            const at = await customerRegister(data);
            localStorage.setItem(TOKEN_KEY, at.accessToken);
            localStorage.setItem(EXPIRES_KEY, at.expiresAt);
            setToken(at.accessToken);
            await refresh(at.accessToken);
            return true;
        } finally {
            setLoading(false);
        }
    }

    async function logout() {
        if (token) await customerLogout(token);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(EXPIRES_KEY);
        setToken(null);
        setCustomer(null);
    }

    return (
        <CustomerContext.Provider
            value={{
                token,
                customer,
                isAuthed,
                loading,
                login,
                register,
                logout,
                refresh,
            }}
        >
            {children}
        </CustomerContext.Provider>
    );
}

export const useCustomer = () => useContext(CustomerContext);
